import { useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Brain, Zap, Lightbulb, ArrowRightLeft, Check, X, Loader2, TrendingUp, AlertTriangle, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const PRIO_COR = {
  alta: "bg-red-50 border-red-300 text-red-800",
  media: "bg-amber-50 border-amber-300 text-amber-800",
  baixa: "bg-slate-50 border-slate-300 text-slate-700",
};
const IMPACTO_ICON = {
  alto: "🚀",
  medio: "📈",
  baixo: "📊",
};
const CONF_COR = {
  alta: "text-emerald-600 bg-emerald-50",
  media: "text-amber-600 bg-amber-50",
  baixa: "text-slate-500 bg-slate-100",
};

/**
 * Botão + Modal do CEAP IA.
 * - Diretoria clica → job assíncrono no backend → polling a cada 5s.
 * - Ao concluir, mostra resumo executivo + insights + oportunidades + trocas sugeridas (aceitar/ignorar).
 * - Aba secundária: chat livre.
 */
export default function CeapIA({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("analise");
  const [gerando, setGerando] = useState(false);
  const [progressoSeg, setProgressoSeg] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [aceitas, setAceitas] = useState(new Set());
  // Chat
  const [chatHist, setChatHist] = useState([]);
  const [pergunta, setPergunta] = useState("");
  const [chatBusy, setChatBusy] = useState(false);

  const pollJob = useCallback(async (jobId) => {
    const start = Date.now();
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      setProgressoSeg(Math.round((Date.now() - start) / 1000));
      try {
        const r = await api.get(`/ai/estrategia-carteira/${jobId}`);
        if (r.data.status === "ok") return r.data.resultado;
        if (r.data.status === "erro") throw new Error(r.data.erro || "Erro na IA");
      } catch (e) {
        if (i > 3) throw e;
      }
    }
    throw new Error("Análise demorou demais. Tente de novo.");
  }, []);

  const gerarAnalise = async () => {
    setGerando(true);
    setResultado(null);
    setProgressoSeg(0);
    setAceitas(new Set());
    try {
      const r = await api.post("/ai/estrategia-carteira");
      const jobId = r.data.job_id;
      const res = await pollJob(jobId);
      setResultado(res);
      toast.success("Análise concluída 🎯");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Erro na IA");
    } finally {
      setGerando(false);
    }
  };

  const aceitarTroca = async (t, idx) => {
    if (aceitas.has(idx)) return;
    try {
      await api.post("/ai/trocas-aceitas", {
        municipio_id: t.municipio_id,
        vendedora_atual_id: t.vendedora_atual_id,
        vendedora_atual_nome: t.vendedora_atual_nome,
        vendedora_sugerida_id: t.vendedora_sugerida_id,
        vendedora_sugerida_nome: t.vendedora_sugerida_nome,
        razao: t.razao,
        municipio_nome: t.municipio_nome,
        municipio_uf: t.municipio_uf,
      });
      setAceitas((s) => new Set([...s, idx]));
      toast.success(`Município ${t.municipio_nome} transferido para ${t.vendedora_sugerida_nome} ✓`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao aceitar troca");
    }
  };

  const enviarPergunta = async () => {
    const p = pergunta.trim();
    if (!p) return;
    setChatHist((h) => [...h, { role: "user", text: p }]);
    setPergunta("");
    setChatBusy(true);
    try {
      const r = await api.post("/ai/chat", { pergunta: p });
      setChatHist((h) => [...h, { role: "ai", text: r.data.resposta }]);
    } catch (e) {
      setChatHist((h) => [...h, { role: "ai", text: `❌ Erro: ${e?.response?.data?.detail || e.message}` }]);
    } finally { setChatBusy(false); }
  };

  const a = resultado?.analise;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 hover:from-indigo-700 hover:to-fuchsia-700 text-white shadow"
        data-testid="btn-ceap-ia"
      >
        <Sparkles className="w-4 h-4 mr-1" /> Estratégia do CEAP IA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
          data-testid="modal-ceap-ia"
        >
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="font-display text-2xl">CEAP IA</span>
              <span className="text-xs text-slate-500 font-normal ml-1">Coach analítico · Claude Sonnet 4.5</span>
            </DialogTitle>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setTab("analise")}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${tab === "analise" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid="ia-tab-analise"
              >
                <TrendingUp className="w-3.5 h-3.5 inline mr-1" /> Análise da carteira
              </button>
              <button
                onClick={() => setTab("chat")}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${tab === "chat" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid="ia-tab-chat"
              >
                <MessageSquare className="w-3.5 h-3.5 inline mr-1" /> Perguntar ao coach
              </button>
            </div>
          </DialogHeader>

          {tab === "analise" ? (
            <div className="flex-1 overflow-auto py-3 space-y-4">
              {!resultado && !gerando && (
                <div className="text-center py-10">
                  <Zap className="w-12 h-12 mx-auto text-indigo-500 mb-3" />
                  <div className="text-lg font-display font-bold text-slate-800">Análise estratégica da carteira</div>
                  <p className="text-sm text-slate-500 max-w-lg mx-auto mt-2">
                    A IA vai analisar o histórico de contatos, inscrições e categorias da carteira e devolver:
                    insights escondidos, oportunidades priorizadas e sugestões de trocas de município entre vendedoras.
                  </p>
                  <p className="text-xs text-slate-400 mt-3">A análise leva cerca de 1 a 2 minutos.</p>
                  <Button
                    onClick={gerarAnalise}
                    className="mt-6 bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 hover:from-indigo-700 hover:to-fuchsia-700 text-white"
                    data-testid="btn-gerar-analise"
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> Gerar análise
                  </Button>
                </div>
              )}

              {gerando && (
                <div className="text-center py-10">
                  <Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin mb-3" />
                  <div className="text-base font-bold text-slate-800">Analisando carteira...</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {progressoSeg}s decorridos · a IA está cruzando histórico, contatos e vendas.
                  </div>
                </div>
              )}

              {a && (
                <>
                  {/* Resumo executivo */}
                  {a.resumo_executivo && (
                    <Card className="p-4 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
                      <div className="text-[11px] uppercase font-bold tracking-widest text-indigo-700 mb-1">Resumo executivo</div>
                      <p className="text-sm text-slate-800 leading-relaxed">{a.resumo_executivo}</p>
                    </Card>
                  )}

                  {/* Insights */}
                  {a.insights?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        <h3 className="font-display font-bold text-slate-900">Insights escondidos</h3>
                      </div>
                      <div className="space-y-2">
                        {a.insights.map((i, k) => (
                          <div key={k} className={`p-3 rounded-lg border ${PRIO_COR[i.prioridade] || PRIO_COR.baixa}`} data-testid={`ia-insight-${k}`}>
                            <div className="font-semibold text-sm flex items-center gap-2">
                              {i.prioridade === "alta" && <AlertTriangle className="w-3.5 h-3.5" />}
                              {i.titulo}
                            </div>
                            <div className="text-sm mt-1 opacity-90">{i.detalhe}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Oportunidades */}
                  {a.oportunidades?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <h3 className="font-display font-bold text-slate-900">Oportunidades priorizáveis</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {a.oportunidades.map((o, k) => (
                          <div key={k} className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/40" data-testid={`ia-oport-${k}`}>
                            <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                              <span>{IMPACTO_ICON[o.impacto_estimado] || "•"}</span>
                              {o.titulo}
                            </div>
                            <div className="text-xs text-slate-700 mt-1">{o.descricao}</div>
                            {o.acao_sugerida && (
                              <div className="text-xs text-emerald-800 mt-2 bg-white/70 rounded px-2 py-1 border border-emerald-100">
                                <b>Ação:</b> {o.acao_sugerida}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trocas sugeridas */}
                  {a.trocas_sugeridas?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowRightLeft className="w-4 h-4 text-purple-500" />
                        <h3 className="font-display font-bold text-slate-900">Trocas de carteira sugeridas</h3>
                      </div>
                      <div className="space-y-2">
                        {a.trocas_sugeridas.map((t, k) => (
                          <div key={k} className="p-3 rounded-lg border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-fuchsia-50" data-testid={`ia-troca-${k}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-slate-900">
                                  {t.municipio_nome} <span className="text-slate-500 font-normal">/ {t.municipio_uf}</span>
                                  <span className={`ml-2 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${CONF_COR[t.confianca] || CONF_COR.baixa}`}>
                                    {t.confianca}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-700 mt-1 flex items-center gap-1 flex-wrap">
                                  <b>{t.vendedora_atual_nome || "— sem vendedora —"}</b>
                                  <ArrowRightLeft className="w-3 h-3" />
                                  <b className="text-emerald-700">{t.vendedora_sugerida_nome}</b>
                                </div>
                                <div className="text-xs text-slate-600 italic mt-1">"{t.razao}"</div>
                              </div>
                              {aceitas.has(k) ? (
                                <span className="text-xs bg-emerald-100 text-emerald-700 font-bold px-2 py-1 rounded flex items-center gap-1">
                                  <Check className="w-3 h-3" /> Aceita
                                </span>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => aceitarTroca(t, k)}
                                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                                  data-testid={`btn-aceitar-troca-${k}`}
                                >
                                  <Check className="w-3 h-3 mr-1" /> Aceitar troca
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Análise por vendedora */}
                  {a.analise_vendedoras?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Brain className="w-4 h-4 text-blue-500" />
                        <h3 className="font-display font-bold text-slate-900">Análise por vendedora</h3>
                      </div>
                      <div className="space-y-1">
                        {a.analise_vendedoras.map((v, k) => (
                          <div key={k} className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                            <b className="text-slate-900">{v.vendedora_nome}:</b> <span className="text-slate-700">{v.observacao}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <Button variant="outline" onClick={gerarAnalise} disabled={gerando} data-testid="btn-regerar-analise">
                      <Sparkles className="w-3 h-3 mr-1" /> Gerar nova análise
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Aba Chat */
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-auto py-3 space-y-2" data-testid="ia-chat-hist">
                {chatHist.length === 0 && (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    Pergunte qualquer coisa sobre a carteira, vendedoras ou histórico.<br />
                    <span className="text-xs italic">Ex.: "Por que a Juliana vende mais do que as demais?" ou "Quais vendedoras tiveram resultado abaixo da média este mês?"</span>
                  </div>
                )}
                {chatHist.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {chatBusy && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 text-slate-500 rounded-2xl px-3 py-2 text-sm flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Analisando...
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t pt-2 flex gap-2">
                <Textarea
                  rows={2}
                  value={pergunta}
                  onChange={(e) => setPergunta(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarPergunta(); } }}
                  placeholder="Pergunte ao CEAP IA..."
                  className="flex-1 resize-none"
                  data-testid="ia-input-pergunta"
                  disabled={chatBusy}
                />
                <Button onClick={enviarPergunta} disabled={chatBusy || !pergunta.trim()} className="bg-indigo-600 hover:bg-indigo-700 self-end" data-testid="btn-ia-enviar">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
